/**
 * Argus — WebRtcStreamPlayer.
 *
 * Bloc 2026-05-10 webrtc-pivot: WebRTC peer connection pentru smooth camera streaming.
 * Înlocuiește MSE+fragmented MP4 pipeline cu RTCPeerConnection nativ — jitter buffer + adaptive
 * bitrate handle la nivel browser, eliminând stuttering vizibil din variable producer rate (camera).
 *
 * Bloc 2026-05-10 v2 fix: ICE candidate buffering. Agent ICE candidates pot ajunge la browser
 * ÎNAINTE ca setRemoteDescription să fie complet. Buffer-uim până la remoteDescriptionSet.
 *
 * Bloc webcam-modal-toolbar-redesign 2026-05-12:
 * - Codec prop (vp8 | h264) forwardat la agent prin webrtc_init payload.
 * - Overlay status RO mapping RTCPeerConnectionState bottom-right (CF-656).
 * - LinearProgress sub video: indeterminate → 100% × 3s → hide la first `connected` (CF-657).
 */

import { useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import { wsClient } from '../lib/wsClient';
import { getIceServers } from '../lib/turn';

interface Props {
  agentId: string;
  cameraId: string;
  microphoneId?: string;
  stunUrl?: string;
  onTerminated?: (reason: string) => void;
  muted?: boolean;
  volume?: number; // 0..1 — bloc webrtc-audio-support 2026-05-11
  flipHorizontal?: boolean; // mirror video CSS — used in webcam context (selfie-style)
  codec?: 'vp8' | 'h264'; // bloc webcam-modal-toolbar-redesign 2026-05-12
}

function generateStreamId(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  arr[6] = ((arr[6] ?? 0) & 0x0f) | 0x70;
  arr[8] = ((arr[8] ?? 0) & 0x3f) | 0x80;
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

// CF-656: mapping RTCPeerConnectionState → text uman RO (null = overlay ascuns).
const STATUS_TEXT: Record<RTCPeerConnectionState, string | null> = {
  new: 'Conectare WebRTC...',
  connecting: 'Conectare WebRTC...',
  connected: null,
  disconnected: 'WebRTC deconectat — reîncercare',
  failed: 'WebRTC eșuat',
  closed: null,
};

// CF-657: progressbar lifecycle states.
type ProgressState = 'indeterminate' | 'complete' | 'hidden';

export function WebRtcStreamPlayer({ agentId, cameraId, microphoneId, stunUrl, onTerminated, muted = false, volume = 1, flipHorizontal = false, codec }: Props): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamIdRef = useRef<string>(generateStreamId());
  const [error, setError] = useState<string | null>(null);
  const [connState, setConnState] = useState<RTCPeerConnectionState>('new');
  const [progressState, setProgressState] = useState<ProgressState>('indeterminate');
  const [statusOpacity, setStatusOpacity] = useState<number>(1);
  // Ref pentru a evita re-trigger 3s timer la fiecare connected state (doar prima dată).
  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // CF-657: lifecycle progressbar. La fiecare tranziție connectionState:
  //   - connecting/new + progressState === 'hidden' (reconnect): reset la 'indeterminate'.
  //   - connected: switch la 'complete' (value 100) + setTimeout 3s → 'hidden'.
  // CF-656: lifecycle overlay opacity:
  //   - connected: 1s opacity 1, then fade-out 300ms (opacity 0).
  //   - alte stări: opacity 1 imediat.
  useEffect(() => {
    if (connState === 'connecting' || connState === 'new') {
      if (progressState === 'hidden') {
        setProgressState('indeterminate');
      }
      setStatusOpacity(1);
    } else if (connState === 'connected') {
      if (progressState !== 'hidden') {
        setProgressState('complete');
        if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
        progressTimerRef.current = setTimeout(() => {
          setProgressState('hidden');
          progressTimerRef.current = null;
        }, 3000);
      }
      // Overlay fade: 1s vizibil → fade 300ms.
      setStatusOpacity(1);
      if (statusFadeTimerRef.current) clearTimeout(statusFadeTimerRef.current);
      statusFadeTimerRef.current = setTimeout(() => {
        setStatusOpacity(0);
        statusFadeTimerRef.current = null;
      }, 1000);
    } else if (connState === 'disconnected' || connState === 'failed') {
      setStatusOpacity(1);
      if (statusFadeTimerRef.current) { clearTimeout(statusFadeTimerRef.current); statusFadeTimerRef.current = null; }
    }
    // Cleanup pe unmount + re-run.
    return () => {
      // Nu clear progressTimerRef aici — alte tranziții se ocupă.
    };
  }, [connState, progressState]);

  // Cleanup timers la unmount.
  useEffect(() => () => {
    if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    if (statusFadeTimerRef.current) clearTimeout(statusFadeTimerRef.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tag = `[WebRtc ${streamIdRef.current.substring(0, 8)}]`;
    console.log(`${tag} mount cam=${cameraId} stun=${stunUrl ?? 'default'} codec=${codec ?? 'vp8'}`);

    // Bloc v1.0.41 2026-05-12 — fallback TURN-only la ICE checking >5s.
    // Win10 + browser-uri Chrome/Edge cu mDNS privacy → host candidates obfuscated `.local`
    // care nu rezolvă cross-network. Force `relay` la retry forţează browserul să emite DOAR
    // candidate TURN relay → P2P prin server (165.232.81.235) reliable cross-network.
    // SYNCRO (same LAN) connects pe prima încercare via mDNS host. Win10 prima încercare
    // fail → 5s timeout → retry cu relay-only → succes.
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let attemptCount = 0;

    const runSetup = async (transportPolicy: RTCIceTransportPolicy): Promise<void> => {
      attemptCount++;
      // Generate fresh streamId pentru fiecare attempt — agent allocates new session.
      streamIdRef.current = generateStreamId();
      const streamId = streamIdRef.current;
      console.log(`${tag} setup attempt=${attemptCount} streamId=${streamId.substring(0,8)} iceTransportPolicy=${transportPolicy}`);

      // ICE buffering: candidates din agent pot sosi ÎNAINTE de setRemoteDescription. Coadă FIFO.
      const pendingAgentIce: RTCIceCandidateInit[] = [];
      let remoteDescriptionSet = false;
      try {
        // Bloc turn-server-secure-creds 2026-05-11: fetch ICE servers (STUN + TURN ephemeral
        // creds dacă backend răspunde). Cache local 4min — re-fetch transparent la expire.
        const iceServers = await getIceServers();
        const pc = new RTCPeerConnection({ iceServers, iceTransportPolicy: transportPolicy });
        pcRef.current = pc;
        console.log(`${tag} RTCPeerConnection created iceServers=${JSON.stringify(iceServers)} policy=${transportPolicy}`);

        // Add a video transceiver explicit recvonly — fără asta, browser nu negociază video.
        pc.addTransceiver('video', { direction: 'recvonly' });
        console.log(`${tag} addTransceiver video recvonly`);

        // Bloc webrtc-audio-support 2026-05-11: dacă microphoneId, adăugăm transceiver audio recvonly.
        // Agent va include audio m-line în offer; SDP answer va match.
        if (microphoneId) {
          pc.addTransceiver('audio', { direction: 'recvonly' });
          console.log(`${tag} addTransceiver audio recvonly (mic=${microphoneId})`);
        }

        pc.ontrack = (e) => {
          console.log(`${tag} ontrack streams=${e.streams.length} kind=${e.track.kind}`);
          const v = videoRef.current;
          if (!v) return;
          if (e.streams.length > 0) {
            v.srcObject = e.streams[0]!;
            console.log(`${tag} video.srcObject set`);
          }
        };
        pc.onconnectionstatechange = () => {
          if (cancelled) return;
          const state = pc.connectionState;
          console.log(`${tag} connectionState=${state} (attempt=${attemptCount})`);
          setConnState(state);
          if (state === 'connected' && fallbackTimer) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
          }
          if (state === 'failed' || state === 'closed') {
            setError(`Connection ${state}`);
            onTerminated?.(state);
          }
          // CF-656/657: NU mai apelăm onTerminated la 'disconnected' (poate fi tranzient + browser reia ICE).
        };
        pc.oniceconnectionstatechange = () => {
          if (cancelled) return;
          console.log(`${tag} iceConnectionState=${pc.iceConnectionState}`);
        };
        pc.onicegatheringstatechange = () => {
          console.log(`${tag} iceGatheringState=${pc.iceGatheringState}`);
        };
        pc.onsignalingstatechange = () => {
          console.log(`${tag} signalingState=${pc.signalingState}`);
        };
        pc.onicecandidate = (e) => {
          if (e.candidate) {
            console.log(`${tag} local ICE candidate type=${e.candidate.type} sdpMid=${e.candidate.sdpMid}`);
            void wsClient.requestWebRtcIce(agentId, streamId, e.candidate.toJSON()).catch((err) => {
              console.warn(`${tag} webrtc_ice send failed`, err);
            });
          } else {
            console.log(`${tag} local ICE gathering complete (null candidate)`);
          }
        };

        // Listen for ICE candidates from agent (push event from wsClient).
        const onAgentIce = (e: Event) => {
          const detail = (e as CustomEvent).detail as { streamId?: string; candidate?: string } | undefined;
          if (!detail || detail.streamId !== streamId || !detail.candidate) return;
          try {
            const init = JSON.parse(detail.candidate) as RTCIceCandidateInit;
            console.log(`${tag} agent ICE candidate received (remoteDescSet=${remoteDescriptionSet})`);
            if (remoteDescriptionSet) {
              void pc.addIceCandidate(init).catch((err) => console.warn(`${tag} addIceCandidate failed`, err));
            } else {
              pendingAgentIce.push(init);
            }
          } catch (err) {
            console.warn(`${tag} addIceCandidate parse failed`, err);
          }
        };
        window.addEventListener('argus:webrtc-ice', onAgentIce);

        console.log(`${tag} requesting webrtc_init... mic=${microphoneId ?? '<none>'} codec=${codec ?? 'vp8'}`);
        // Extract TURN entry din iceServers fetch-ed (dacă există) + forward la agent prin IPC.
        // Agent foloseşte aceleaşi creds pentru a se autentifica la coturn (single-shot per session).
        const turnEntry = iceServers.find((s) => {
          const u = typeof s.urls === 'string' ? s.urls : (s.urls?.[0] ?? '');
          return u.startsWith('turn:') && s.username && s.credential;
        });
        const turn = turnEntry
          ? {
              urls: (typeof turnEntry.urls === 'string' ? turnEntry.urls : (turnEntry.urls?.[0] ?? '')),
              username: turnEntry.username ?? '',
              credential: typeof turnEntry.credential === 'string' ? turnEntry.credential : '',
            }
          : undefined;
        const initResponse = await wsClient.requestWebRtcInit(agentId, streamId, cameraId, stunUrl, microphoneId, turn, codec);
        if (cancelled) {
          window.removeEventListener('argus:webrtc-ice', onAgentIce);
          return;
        }
        console.log(`${tag} offer received sdpLen=${initResponse.sdp.length}`);

        await pc.setRemoteDescription({ type: 'offer', sdp: initResponse.sdp });
        remoteDescriptionSet = true;
        console.log(`${tag} setRemoteDescription OK; flushing ${pendingAgentIce.length} buffered ICE candidates`);

        // Flush buffered ICE candidates.
        for (const init of pendingAgentIce) {
          void pc.addIceCandidate(init).catch((err) => console.warn(`${tag} buffered addIceCandidate failed`, err));
        }
        pendingAgentIce.length = 0;

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        console.log(`${tag} answer created + setLocalDescription OK sdpLen=${answer.sdp?.length}`);

        await wsClient.requestWebRtcAnswer(agentId, streamId, answer.sdp ?? '');
        console.log(`${tag} answer sent to agent OK`);

        (pc as RTCPeerConnection & { _cleanupListener?: () => void; _streamId?: string })._cleanupListener = () =>
          window.removeEventListener('argus:webrtc-ice', onAgentIce);
        (pc as RTCPeerConnection & { _cleanupListener?: () => void; _streamId?: string })._streamId = streamId;

        // Bloc v1.0.41 — 5s fallback timer. Dacă connectionState rămâne în new|connecting|checking
        // după 5s, recreate cu iceTransportPolicy='relay' (force TURN-only).
        if (attemptCount === 1) {
          fallbackTimer = setTimeout(() => {
            if (cancelled) return;
            if (pc.connectionState === 'connected' || pc.connectionState === 'closed') return;
            console.warn(`${tag} ICE timeout 5s in state=${pc.connectionState} — fallback iceTransportPolicy=relay`);
            // Close current pc + close session pe agent (separate streamId) → retry cu relay-only.
            const prevStreamId = (pc as RTCPeerConnection & { _streamId?: string })._streamId ?? '';
            const cleanup = (pc as RTCPeerConnection & { _cleanupListener?: () => void })._cleanupListener;
            cleanup?.();
            try { pc.close(); } catch { /* ignore */ }
            if (prevStreamId) {
              void wsClient.requestWebRtcClose(agentId, prevStreamId).catch(() => { /* best effort */ });
            }
            pcRef.current = null;
            void runSetup('relay');
          }, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'WebRTC setup failed';
        console.error(`${tag} setup failed`, err);
        setError(msg);
        onTerminated?.(msg);
      }
    };
    void runSetup('all');

    return () => {
      cancelled = true;
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      console.log(`${tag} unmount`);
      const pc = pcRef.current;
      const closedStreamId = pc ? (pc as RTCPeerConnection & { _streamId?: string })._streamId ?? streamIdRef.current : streamIdRef.current;
      if (pc) {
        const cleanup = (pc as RTCPeerConnection & { _cleanupListener?: () => void })._cleanupListener;
        cleanup?.();
        try { pc.close(); } catch { /* ignore */ }
      }
      pcRef.current = null;
      void wsClient.requestWebRtcClose(agentId, closedStreamId).catch(() => { /* best effort */ });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, cameraId, microphoneId, codec]);

  // Bloc webrtc-audio-support 2026-05-11: propagă volume la `<video>.volume`. muted prop deja propagat.
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume]);

  const statusText = error ? `WebRTC: ${error}` : STATUS_TEXT[connState];
  const overlayVisible = statusText !== null && statusOpacity > 0;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', backgroundColor: '#000', position: 'relative' }}>
      <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={muted}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: flipHorizontal ? 'scaleX(-1)' : undefined }}
        />
        {overlayVisible && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              padding: '4px 8px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              fontSize: 12,
              borderRadius: 1,
              opacity: statusOpacity,
              transition: 'opacity 300ms',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {statusText}
          </Box>
        )}
      </Box>
      {/* CF-657: LinearProgress sub video, width=100% match container. */}
      {progressState !== 'hidden' && (
        <LinearProgress
          variant={progressState === 'indeterminate' ? 'indeterminate' : 'determinate'}
          value={progressState === 'complete' ? 100 : undefined}
          sx={{ width: '100%', height: 3, flexShrink: 0 }}
        />
      )}
    </Box>
  );
}
